import { useState } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Skeleton, SkeletonGroup } from '../Skeleton';
import { ZoneSheet } from './ZoneSheet';
import { CARD, SECTION_HEADER, TEXT_ACTION } from '../live/surfaces';
import { chipParts, dayKey, dayTitle, monthGrid, shortDay, timeLabel, whenLine, zoneName } from '../../utils/liveCalendar';
import { openDay, pickerDays } from '../../utils/calendarPicker';

/**
 * The calendar picker (components.md 6, decision 156): the booking
 * calendar, and the only way a time is picked, for booking, Pick a New
 * Time, Pick a Time and the coach's Suggest New Times. The month in a white
 * card, the zone line under it, then the chosen day's times beneath, three
 * to a row. It opens on today when today has times, else the first later
 * day that has; past days are disabled and past times absent, earlier today
 * included, so a day with only past times shows as having none. The times
 * are the caller's, from the server's slot grid; nothing is invented here.
 */

const FOOTNOTE = 'text-[13px] leading-[18px] text-db-text-2';
const CHIP =
  'ios-press h-11 cursor-pointer rounded-[100px] text-[15px] leading-[20px] font-medium tabular-nums ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 ';
/** Chosen: the tint wash with a biscayne ring, so the confirm stays the one fill (audit S4). */
const CHIP_ON = 'bg-[rgba(26,107,138,0.12)] text-biscayne ring-[1.5px] ring-inset ring-biscayne';
const CHIP_OFF = 'border border-[rgba(0,0,0,0.08)] bg-white text-db-text-1';

export interface PickerTime {
  id: string;
  startAt: string;
}

function ArrowButton({ dir, label, disabled, onClick }: {
  dir: 'prev' | 'next'; label: string; disabled: boolean; onClick: () => void;
}) {
  const Icon = dir === 'prev' ? CaretLeft : CaretRight;
  return (
    // Not 111's call bar: components.md 6 (decision 156) draws the month arrows as 36 circles on desktop too, the
    // booking calendar's own; logged as proposed in status/decisions.md.
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={
        'ios-press relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-full ' +
        'bg-[rgba(120,120,128,0.16)] text-db-text-1 disabled:cursor-default disabled:opacity-30 ' +
        "after:absolute after:-inset-1 after:content-[''] " +
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40'
      }
    >
      <Icon size={16} weight="bold" aria-hidden="true" />
    </button>
  );
}

/**
 * One day of the month: a biscayne-tinted circle with open times, solid
 * when chosen, ringed when it is today, greyed otherwise. A greyed day in
 * the window can still be tapped, to say so; one outside it cannot.
 */
function DayCell({ dayKey: key, inWindow, selected, today, hasTimes, known, onClick }: {
  dayKey: string; inWindow: boolean; selected: boolean; today: boolean; hasTimes: boolean;
  /** The open times have loaded, so "none" means none. */
  known: boolean;
  onClick: () => void;
}) {
  const open = known && hasTimes;
  const look = selected ? 'bg-biscayne font-semibold text-white'
    : open ? 'bg-[rgba(26,107,138,0.12)] font-semibold text-biscayne'
    : inWindow && !known ? 'text-db-text-1'
    : 'text-db-text-3';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!inWindow}
      aria-pressed={selected}
      aria-current={today ? 'date' : undefined}
      aria-label={`${dayTitle(key)}${today ? ', today' : ''}${known && inWindow && !hasTimes ? ', no open times' : ''}`}
      data-picker-day={key}
      className={
        'ios-press relative mx-auto flex h-10 w-10 items-center justify-center rounded-full text-[17px] leading-[22px] tracking-[-0.43px] tabular-nums ' +
        "after:absolute after:-inset-0.5 after:content-[''] " +
        'cursor-pointer disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 ' +
        `${look} ${today && !selected ? 'ring-[1.5px] ring-inset ring-biscayne' : ''}`
      }
    >
      {chipParts(key).day}
    </button>
  );
}

/** "Times in Eastern Time · Change": Change opens the app's time zone sheet, which applies the zone at once and saves it behind. */
function ZoneLine({ zone, onChange }: { zone: string; onChange: (zone: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <p className={`text-center ${FOOTNOTE}`}>
        Times in {zoneName(zone)} ·{' '}
        <button type="button" className={TEXT_ACTION} aria-haspopup="dialog" onClick={() => setOpen(true)}>Change</button>
      </p>
      {open && <ZoneSheet zone={zone} onSaved={onChange} onClose={() => setOpen(false)} />}
    </div>
  );
}

export function CalendarPicker<T extends PickerTime>({
  days, times, timeZone, onZoneChange, state, onRetry, selectedIds, onPick, usual, disabledIds, nowMs, full = false,
}: {
  /** The window's day keys, today first (bookingDays, requestDays). */
  days: string[];
  /** The times the server's grid offers; past ones are dropped here. */
  times: T[];
  timeZone: string;
  onZoneChange: (zone: string) => void;
  /** Only the times wait: the month draws at once. */
  state: 'loading' | 'ready' | 'error';
  onRetry?: () => void;
  selectedIds: string[];
  onPick: (time: T) => void;
  /** "Your usual time": the member's weekly time, first when it is open. */
  usual?: T | null;
  /** DEV ONLY: LIVE_DEV_ANY_TIME. Drawn times the server would refuse, kept greyed. */
  disabledIds?: Set<string>;
  nowMs?: number;
  /** As many picked as the caller takes (Suggest New Times' three): the rest wait. */
  full?: boolean;
}) {
  const [mountNow] = useState(() => Date.now());
  const now = nowMs ?? mountNow;
  const known = state === 'ready';
  const { byDay, hasTimes } = pickerDays(days, times, timeZone, now, disabledIds);
  const months = [...new Set(days.map((d) => d.slice(0, 7)))];

  // The chosen day: the one tapped, else a chosen time's, else today or the first later day with times.
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  const chosenDay = selectedIds.map((id) => times.find((t) => t.id === id)).find(Boolean);
  const day = (pickedDay && days.includes(pickedDay) ? pickedDay : null)
    ?? (chosenDay && days.includes(dayKey(chosenDay.startAt, timeZone)) ? dayKey(chosenDay.startAt, timeZone) : null)
    ?? openDay(days, hasTimes, known);
  const [monthOverride, setMonthOverride] = useState<string | null>(null);
  const month = monthOverride && months.includes(monthOverride) ? monthOverride : day.slice(0, 7);
  const monthIndex = months.indexOf(month);
  const pickDay = (key: string) => { setPickedDay(key); setMonthOverride(null); };
  const pick = (t: T) => { onPick(t); pickDay(dayKey(t.startAt, timeZone)); };
  const changeZone = (z: string) => { onZoneChange(z); setPickedDay(null); setMonthOverride(null); };

  const dayTimes = byDay.get(day) ?? [];
  const nextOpen = days.find((d) => d > day && hasTimes(d)) ?? null;
  const monthHasTimes = known && days.some((d) => d.startsWith(month) && hasTimes(d));
  const usualShown = usual && usual.startAt > new Date(now).toISOString() ? usual : null;

  return (
    <div className="mx-auto w-full max-w-[560px]" data-calendar-picker>
      <section aria-labelledby="select-day">
        <h2 id="select-day" className={SECTION_HEADER}>Select a day</h2>
        <div className={`p-4 ${CARD}`}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[17px] leading-[22px] font-semibold tracking-[-0.43px] text-db-text-1">
              {chipParts(`${month}-01`).month}
            </h3>
            <div className="flex gap-3">
              <ArrowButton dir="prev" label="Previous month" disabled={monthIndex <= 0}
                onClick={() => setMonthOverride(months[monthIndex - 1])} />
              <ArrowButton dir="next" label="Next month" disabled={monthIndex < 0 || monthIndex >= months.length - 1}
                onClick={() => setMonthOverride(months[monthIndex + 1])} />
            </div>
          </div>
          <div className="grid grid-cols-7" aria-hidden="true">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <span key={i} className="pb-2 text-center text-[13px] leading-[18px] text-db-text-2">{d}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-y-1.5">
            {monthGrid(month).flat().map(({ key, inMonth }) => (
              inMonth ? (
                <DayCell key={key} dayKey={key} inWindow={days.includes(key)} selected={key === day}
                  today={key === days[0]} hasTimes={hasTimes(key)} known={known} onClick={() => pickDay(key)} />
              ) : <span key={key} />
            ))}
          </div>
          {known && !monthHasTimes && (
            <p className="mt-3 text-center text-[15px] leading-[20px] text-db-text-2" data-month-empty>No open times this month.</p>
          )}
        </div>
        <ZoneLine zone={timeZone} onChange={changeZone} />
      </section>

      {/* The chosen day's times, beneath the month, three to a row */}
      <section aria-labelledby="select-time" aria-live="polite" className="mt-8">
        <h2 id="select-time" className={SECTION_HEADER}>Select a time</h2>
        <div className={`p-4 ${CARD}`}>
          <h3 className="mb-3 text-[15px] leading-[20px] font-semibold text-db-text-1">{dayTitle(day)}</h3>
          {state === 'loading' ? (
            <SkeletonGroup label="Loading open times">
              <div className="grid grid-cols-3 gap-2">
                {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} width="100%" height={44} radius={22} />)}
              </div>
            </SkeletonGroup>
          ) : state === 'error' ? (
            <p className="text-[15px] leading-[20px] text-db-text-2">
              Could not load open times.{' '}
              {onRetry && <button type="button" className={TEXT_ACTION} onClick={onRetry}>Try Again</button>}
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2" data-picker-times={day}>
              {usualShown && (
                <button type="button" aria-pressed={selectedIds.includes(usualShown.id)} onClick={() => pick(usualShown)}
                  className={`${CHIP} col-span-3 w-full px-4 ${selectedIds.includes(usualShown.id) ? CHIP_ON : CHIP_OFF}`}>
                  Your usual time · {whenLine(usualShown.startAt, timeZone)}
                </button>
              )}
              {dayTimes.length ? dayTimes.map((s) => {
                const on = selectedIds.includes(s.id);
                const greyed = disabledIds?.has(s.id) ?? false;
                return (
                  // DEV ONLY: LIVE_DEV_ANY_TIME. A drawn time that is past or clashes is kept, greyed.
                  <button key={s.id} type="button" aria-pressed={on} onClick={() => pick(s)}
                    disabled={greyed} aria-disabled={full && !on ? true : undefined}
                    data-any-time={disabledIds ? (greyed ? 'greyed' : 'open') : undefined} data-picker-time={s.startAt}
                    className={`${CHIP} w-full ${on ? CHIP_ON : CHIP_OFF} ${full && !on ? 'cursor-not-allowed' : ''} disabled:cursor-not-allowed disabled:border-transparent disabled:bg-[rgba(120,120,128,0.12)] disabled:text-db-text-3`}>
                    {timeLabel(s.startAt, timeZone)}
                  </button>
                );
              }) : (
                <p className="col-span-3 text-[15px] leading-[20px] text-db-text-2" data-day-empty>
                  No open times on {shortDay(day)}.
                  {nextOpen && (
                    <>
                      {' '}Next:{' '}
                      <button type="button" className={TEXT_ACTION} onClick={() => pickDay(nextOpen)}>{shortDay(nextOpen)}</button>
                    </>
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
